import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Check, ChevronRight } from "lucide-react";
import confetti from 'canvas-confetti';
import axios from "axios";

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [subscription, setSubscription] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  
  // Extract query parameters
  const params = new URLSearchParams(location.search);
  const sessionId = params.get('session_id');
  
  useEffect(() => {
    // Launch confetti effect
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 }
    });
    
    const fetchSubscriptionDetails = async () => {
      try {
        if (sessionId) {
          // If we have a session ID, verify it
          const response = await axios.post('/api/payments/verify', {
            provider: 'stripe',
            paymentData: { session_id: sessionId }
          });
          
          if (response.data.success) {
            setSubscription(response.data.subscription);
          }
        } else {
          // Otherwise just fetch the latest company subscription
          const response = await axios.get('/api/companies/my-subscription');
          setSubscription(response.data);
        }
      } catch (error) {
        console.error('Error fetching subscription details:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchSubscriptionDetails();
  }, [sessionId]);
  
  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-green-100 p-2 flex items-center justify-center">
            <Check className="h-8 w-8 text-green-600" />
          </div>
          <CardTitle className="text-2xl font-bold">Payment Successful!</CardTitle>
        </CardHeader>
        <CardContent className="text-center space-y-2 pb-2">
          <p>
            Thank you for your payment. Your account has been activated with the selected plan.
          </p>
          {subscription && (
            <div className="mt-6 p-4 bg-gray-50 rounded-md">
              <h3 className="font-medium">Subscription Details</h3>
              <div className="mt-2 space-y-1 text-sm">
                <p>
                  <span className="text-muted-foreground">Plan:</span>{" "}
                  <span className="font-medium capitalize">{subscription.plan}</span>
                </p>
                <p>
                  <span className="text-muted-foreground">Valid Until:</span>{" "}
                  <span className="font-medium">
                    {new Date(subscription.endDate).toLocaleDateString()}
                  </span>
                </p>
                <p>
                  <span className="text-muted-foreground">Candidate Limit:</span>{" "}
                  <span className="font-medium">{subscription.candidateLimit}</span>
                </p>
              </div>
            </div>
          )}
        </CardContent>
        <CardFooter className="flex flex-col space-y-2 pt-0">
          <Button 
            onClick={() => navigate('/dashboard')} 
            className="w-full"
          >
            Go to Dashboard <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
          <Button 
            variant="outline" 
            onClick={() => navigate('/account/billing')} 
            className="w-full"
          >
            View Billing Details
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
};

export default PaymentSuccess;
